import React, { useEffect, useRef } from "react";

type Props = {
  workbook: string;
  view: string;
  height?: string | number;
  width?: string | number;
};

const TableauEmbed: React.FC<Props> = ({
  workbook,
  view,
  height = "800px",
  width = "100%",
}) => {
  const vizRef = useRef<HTMLDivElement>(null);
  const vizObj = useRef<any>(null);
  const apiBase = import.meta.env.VITE_API_URL as string;

  useEffect(() => {
    console.info("[TableauEmbed] using", { workbook, view, height, width, apiBase });
    
    const ac = new AbortController();
    let disposed = false;

    (async () => {
      try {
        const wb = (workbook ?? "").trim();
        const vw = (view ?? "").trim();

        if (!wb || !vw) {
          console.warn("[TableauEmbed] workbook/view missing");
          return;
        }

        const cid = window.crypto?.randomUUID?.() ?? String(Date.now());
        const res = await fetch(
          `${apiBase}/tableau-dashboard/trusted-url?workbook=${encodeURIComponent(
            wb
          )}&view=${encodeURIComponent(vw)}&showVizHome=true`,
          { cache: "no-store", headers: { "x-correlation-id": cid }, signal: ac.signal }
        );

        if (!res.ok) {
          const txt = await res.text().catch(() => "");
          throw new Error(`trusted-url failed: ${res.status} ${txt}`);
        }

        const { url } = (await res.json()) as { url: string };
        if (disposed || !url || !vizRef.current) return;

        const tableauUrl = url.includes("?")
          ? `${url}&:tabs=no`
          : `${url}?:tabs=no`;

        const tableau = (window as any).tableau;
        if (!tableau?.Viz) {
          throw new Error("Tableau v2 API not available on window.tableau");
        }

        // Dispose any previous viz
        try {
          vizObj.current?.dispose?.();
        } catch {
          /* ignore */
        }

        vizObj.current = new tableau.Viz(vizRef.current, tableauUrl, {
          width: String(width),
          height: String(height),
          hideTabs: true,
          hideToolbar: true, // hide the top toolbar visible
          onFirstInteractive: () => console.info("[TableauEmbed] Viz interactive"),
        });
      } catch (err: any) {
        if (err?.name === "AbortError") return;
        console.error("[TableauEmbed] Fatal error:", err);
      }
    })();

    return () => {
      disposed = true;
      ac.abort();
      try {
        vizObj.current?.dispose?.();
      } catch {/* ignore */}
    };
  }, [workbook, view, height, width, apiBase]);

  return <div ref={vizRef} style={{ width, height }} />;
};

export default TableauEmbed;
